import { useRef, useSyncExternalStore } from 'react';
import { Id, toast } from 'react-toastify';
import * as filestack from 'filestack-js';
import { BRAND_KIT_PATH } from '@goldcast/player-config/src/constants/brandKit.constants';
import { brandKit, clipsBrandKit, storeBrandKitChanges, updateIndividualBrandKitConfig } from '@/stores/brandKit';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/libs/toast/toast';
import { getEnvConfig } from '@/constants';
import { currentUser } from '@/stores/user';
import { getFilesForUpload, getUploadConfig, verifyFontFile } from '@/Pages/BrandKit/components/utils';
import { BRAND_KIT_MAX_FILES, BRAND_KIT_MAX_FILE_SIZE } from '@/Pages/BrandKit/constants';
import { useAppContext } from '@/context/AppContext/AppContext';
import { uploadFilesWithFilestackSecurityRetry } from '@/utils/filestackSecurity';
import { getErrorDiagnostics } from '@/libs/errorDiagnostics';

const BRAND_KIT_ASSET_KIND_BY_KEY: Record<string, string> = {
  fonts: 'font',
  background: 'image',
  intro_outro: 'video'
};

export default function useBrandKitComponent({
  key,
  title,
  clipId,
  maxFileSize = BRAND_KIT_MAX_FILE_SIZE,
  onUploadComplete
}: {
  key: string;
  title: string;
  clipId?: string;
  maxFileSize?: number;
  /** When provided, replaces the default local-store update + storeBrandKitChanges persistence step. Receives the renamed file paths. */
  onUploadComplete?: (filePaths: string[]) => Promise<void>;
}) {
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const clipsBrandKitStore = useSyncExternalStore(clipsBrandKit.subscribe, clipsBrandKit.getSnapshot);
  const activeStore = clipId ? clipsBrandKitStore[clipId] : brandKitStore;
  const { logger } = useAppContext();
  const uploadToastId = useRef<Id | undefined>();
  const baseFilePath = clipId
    ? `${currentUser.getSnapshot()?.organization}/clips/${clipId}`
    : `${currentUser.getSnapshot()?.organization}`;

  function onFilesUpload(files: File[]): Promise<any> {
    const totalSize = files.reduce((acc, f) => f.size + acc, 0);
    if (files.length > BRAND_KIT_MAX_FILES) {
      showErrorToast('Please select up to 10 files');
      return Promise.reject();
    }
    const filesToUpload = getFilesForUpload(key, files, maxFileSize);
    if (filesToUpload.length < files.length) {
      showWarningToast(`Some files are ignored due to ${maxFileSize}MB file size limit`);
    }
    if (!filesToUpload.length) {
      return Promise.reject();
    }
    return uploadBrandKitFiles(filesToUpload, getUploadConfig(uploadToastId, totalSize))
      .catch(err => {
        toast.dismiss(uploadToastId.current);
        uploadToastId.current = undefined;
        showErrorToast('Failed to upload file');
        logger.error('[useBrandKitComponent] Failed to upload brand kit file', {
          clipId,
          assetKind: BRAND_KIT_ASSET_KIND_BY_KEY[key],
          ...getErrorDiagnostics(err)
        });
        return Promise.reject(err);
      })
      .then(() => {
        toast.dismiss(uploadToastId.current);
        uploadToastId.current = undefined;
        return verifyUploadedFiles(filesToUpload)
          .then(() => {
            const filePaths = filesToUpload.map(f => f.name);
            const persistFn = onUploadComplete
              ? onUploadComplete(filePaths)
              : (updateIndividualBrandKitConfig(key, [...activeStore[key], ...filePaths], clipId),
                storeBrandKitChanges(clipId));
            return Promise.resolve(persistFn)
              .then(() => {
                showSuccessToast(`${title} updated successfully`);
                return filesToUpload;
              })
              .catch(() => Promise.reject());
          })
          .catch(err => {
            logger.error('[useBrandKitComponent] Failed to update brand kit', {
              clipId,
              assetKind: BRAND_KIT_ASSET_KIND_BY_KEY[key],
              ...getErrorDiagnostics(err)
            });
            if (err) {
              showErrorToast(err);
            } else {
              return Promise.reject('Error storing file');
            }
          });
      });
  }

  function verifyUploadedFiles(filesToUpload: File[]): Promise<any> {
    if (key === 'fonts' && !!filesToUpload.length) {
      return verifyFontFile(filesToUpload[0].name);
    }
    return Promise.resolve();
  }

  function deleteItem(name: string) {
    updateIndividualBrandKitConfig(
      key,
      activeStore[key].filter(v => v !== name),
      clipId
    );
    storeBrandKitChanges(clipId);
  }

  async function uploadBrandKitFiles(files: File[], uploadConfig: filestack.UploadOptions): Promise<any> {
    const filePath = BRAND_KIT_PATH;
    const storeConfig: filestack.StoreUploadOptions = {
      location: 's3',
      container: getEnvConfig('S3_STATIC_ASSETS_BUCKET'),
      path: `/${filePath}`,
      disableStorageKey: true,
      region: getEnvConfig('AWS_STORE_REGION'),
      filename: file => `${baseFilePath}/${file.name}`
    };
    const client = filestack.init(getEnvConfig('FILESTACK_API_KEY'));

    return uploadFilesWithFilestackSecurityRetry({
      client,
      files,
      uploadOptions: uploadConfig,
      storeOptions: storeConfig,
      filestackIntent: {
        intent: 'brand_kit_asset',
        assetKind: BRAND_KIT_ASSET_KIND_BY_KEY[key] || 'image'
      }
    });
  }

  return {
    onFilesUpload,
    deleteItem,
    brandKitStore,
    clipsBrandKitStore
  };
}
